// src/tenants/components/TenantNavBar.jsx

import React from 'react';
import { BarChart3, Users, Settings } from 'lucide-react';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import { FirebaseService } from '../../shared/services/FirebaseService';
import TenantUserManagement from './TenantUserManagement';
import SystemSwitcher from './SystemSwitcher';

/**
 * Catalog of supported nav tabs. A tenant config's `tabs` array selects
 * from these by id. Future tabs (routing, chainto, documents, etc.) are
 * added here when a registered tenant needs them. Today's only registered
 * tenant (InMocean) uses dashboard / team / settings.
 */
const TAB_CATALOG = {
    dashboard: { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    team:      { id: 'team',      label: 'Team',      icon: Users },
    settings:  { id: 'settings',  label: 'Settings',  icon: Settings },
};

/**
 * TenantNavBar — generic tenant chrome driven by per-tenant config.
 *
 * Renders the same nav shell that lived inline in TenantRouter.jsx
 * pre-CHA-63 (the SPECIAL CASE InMocean block at L184-332), parameterized
 * by tenantConfig.brand and tenantConfig.tabs. Renders the tenant's
 * AppComponent (or a tab-specific view: TenantUserManagement for 'team',
 * a placeholder for 'settings') in the main content area.
 *
 * Per CLAUDE.md §5.1 (C operating model), this component contains zero
 * tenant-specific logic. All tenant-specific values flow in via
 * tenantConfig. To onboard a new tenant, add a config file at
 * src/tenants/<tenant>/config.js and register it in src/tenants/registry.js.
 *
 * Notable parity adjustments vs the pre-CHA-63 InMocean block:
 *   - Status indicator now reads from tenant.status (matching the existing
 *     general-tenant fallback path L446-451) instead of the hardcoded
 *     green dot. The original InMocean inline UI was a regression vs the
 *     general path; bringing them into alignment was free with this refactor.
 *   - Settings view text is now `${brand.title} Settings` instead of
 *     hardcoded "InMocean Settings" — generalize per CHA-63 Stage C-2.
 *   - isPlatformOwner computed once at the top instead of inlined ~7
 *     times. Same behavior, less duplication.
 */
export default function TenantNavBar({
    tenantConfig,
    companyId,
    tenant,
    userProfile,
    activeApp,
    onTabChange,
    onReturnToPlatform,
}) {
    const isPlatformOwner = userProfile?.role === 'platform_owner';
    const isAdminOrPlatformOwner = userProfile?.role === 'admin' || isPlatformOwner;
    const handleSignOut = () => FirebaseService.signOut();

    const enabledTabs = (tenantConfig.tabs || [])
        .map((tabId) => TAB_CATALOG[tabId])
        .filter(Boolean);

    const AppComponent = tenantConfig.AppComponent;

    return (
        <ErrorBoundary fallbackMessage={`Something went wrong with the ${tenantConfig.brand.title} application`}>
            <div className="min-h-screen bg-gray-50">
                {/* Tenant Navigation */}
                <nav className="bg-white border-b border-gray-200">
                    <div className="max-w-[1600px] mx-auto px-8 py-10 space-y-10">
                        <div className="flex justify-between items-center py-3">
                            <div className="flex items-center gap-3">
                                {/* CHA-556: no back arrow. It called onReturnToPlatform, but
                                    only platform_owner can access the platform — regular tenant
                                    users (powerbi/user/viewer/admin) were sent to a surface they
                                    have no access to. platform_owner switches via onSystemSwitch
                                    (below), so the arrow served no one. Header only, all roles. */}
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">{tenantConfig.brand.emoji}</span>
                                    <div>
                                        <h2 className="font-semibold text-gray-900">{tenantConfig.brand.title}</h2>
                                        <p className="text-sm text-gray-600">{tenantConfig.brand.subtitle}</p>
                                    </div>
                                </div>

                                {/* Navigation Tabs — Platform Owners Only */}
                                <div className="hidden md:flex items-center gap-1 ml-8">
                                    {isPlatformOwner && enabledTabs.map((tab) => {
                                        const Icon = tab.icon;
                                        return (
                                            <button
                                                key={tab.id}
                                                onClick={() => onTabChange(tab.id)}
                                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                    activeApp === tab.id
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                                }`}
                                            >
                                                <Icon size={16} className="inline mr-2" />
                                                {tab.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* User Area with System Switcher for Platform Owner */}
                            <div className="flex items-center gap-3">
                                <div className="text-right">
                                    <div className="text-sm font-medium text-gray-900">
                                        {userProfile?.email || 'User'}
                                    </div>
                                    <div className="text-xs text-gray-600 capitalize">
                                        {userProfile?.role?.replace('_', ' ') || 'User'}
                                    </div>
                                </div>
                                <div className="text-xs text-gray-600 flex items-center gap-1">
                                    <div className={`w-2 h-2 rounded-full ${
                                        tenant?.status === 'active' ? 'bg-green-500' :
                                        tenant?.status === 'inactive' ? 'bg-gray-400' :
                                        tenant?.status ? 'bg-red-500' : 'bg-gray-400'
                                    }`}></div>
                                    {tenant?.status || 'unknown'}
                                </div>

                                {/* Platform Owner: System Switcher Dropdown */}
                                {isPlatformOwner ? (
                                    <SystemSwitcher
                                        currentSystem={tenantConfig.brand.title}
                                        currentSystemId={tenantConfig.id}
                                        onSystemSwitch={onReturnToPlatform}
                                        onSignOut={handleSignOut}
                                    />
                                ) : (
                                    <button
                                        onClick={handleSignOut}
                                        className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                                    >
                                        Sign Out
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </nav>

                {/* App Content */}
                <main className="max-w-[1600px] mx-auto px-8 py-10 space-y-10">
                    {activeApp === 'team' && isAdminOrPlatformOwner ? (
                        <TenantUserManagement
                            companyId={companyId}
                            userProfile={userProfile}
                            companyData={tenant}
                        />
                    ) : activeApp === 'settings' ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tenantConfig.brand.title} Settings</h3>
                            <p className="text-gray-600">Company settings and configuration options will be available here.</p>
                        </div>
                    ) : (
                        <AppComponent
                            user={{ email: userProfile?.email || 'User' }}
                            userProfile={userProfile}
                            tenantConfig={tenantConfig}
                        />
                    )}
                </main>
            </div>
        </ErrorBoundary>
    );
}
